import React from "react";

export type SimpleTreeNode = {
  id: string;
  pId?: string | null;
  title: string;
  isLeaf?: boolean;
  isCompany?: boolean;
  key: string;
  hideEmployeeIcon?: boolean; // Flag để ẩn icon nhân viên
};

/**
 * Kiểm tra xem ancestor có phải là tổ tiên của descendant không
 */
export const isAncestor = (ancestor: any, descendant: any, allDeps: any[]): boolean => {
  if (!ancestor || !descendant || !allDeps) return false;
  if (ancestor.ID === descendant.ID) return true; // Chính nó cũng được coi là ancestor

  let currentId = descendant.ParentID || descendant.parentID;
  const visited = new Set<string>();
  let guard = 0;

  while (currentId && currentId !== '0' && guard++ < 1000) {
    if (visited.has(currentId)) break; // Tránh vòng lặp vô hạn
    visited.add(currentId);

    if (String(currentId) === String(ancestor.ID)) {
      return true;
    }

    const currentDept = allDeps.find((d: any) => String(d.ID) === String(currentId));
    if (!currentDept) break;
    currentId = currentDept.ParentID || currentDept.parentID;
  }

  return false;
};

/**
 * Lấy tất cả descendant IDs của một node
 */
export const getDescendantIds = (nodeId: string, allNodes: SimpleTreeNode[]): string[] => {
  const result: string[] = [];
  const queue = [nodeId];
  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const children = allNodes.filter((n) => n.pId === currentId);
    children.forEach((child) => {
      result.push(child.id);
      queue.push(child.id);
    });
  }
  return result;
};

/**
 * Lấy parent ID của một node
 */
export const getParentId = (nodeId: string, allNodes: SimpleTreeNode[]): string | undefined => {
  const node = allNodes.find((n) => n.id === nodeId);
  return node?.pId ?? undefined;
};

/**
 * Lấy siblings (cùng parentID)
 */
export const getSiblingIds = (nodeId: string, allNodes: SimpleTreeNode[]): string[] => {
  const node = allNodes.find((n) => n.id === nodeId);
  if (!node?.pId) return [];
  return allNodes.filter((n) => n.pId === node.pId && n.id !== nodeId).map((n) => n.id);
};

/**
 * Lấy list ancestor ids từ root -> depId dựa vào ParentID/TreePath
 */
export const getAncestorIds = (depId: string, departments: any[]): string[] => {
  if (!depId || !departments?.length) return [];
  const map = new Map<string, any>();
  departments.forEach((d: any) => {
    const id = String(d.ID ?? d.id);
    map.set(id, d);
  });

  const result: string[] = [];
  let currentId: string | undefined = depId;
  let guard = 0;

  while (currentId && guard++ < 1000) {
    const node = map.get(currentId);
    if (!node) break;
    const parentId = String(node.ParentID ?? node.parentID ?? '');
    if (!parentId || parentId === '0' || parentId === currentId) break;
    result.unshift(parentId);
    currentId = parentId;
  }

  return result;
};
    
export const extractTextLabel = (label: any): string => {
  if (typeof label === 'string') return label;
  if (React.isValidElement(label) && (label as React.ReactElement).props?.children) {
    const c = (label as React.ReactElement).props.children;
    if (typeof c === 'string') return c;
    if (Array.isArray(c)) {
      const textChild = c.find((x: any) => typeof x === 'string');
      if (textChild) return textChild;
    }
  }
  return String(label ?? '');
};
